/** @jsxRuntime classic */
/** @jsx jsx */
import { memo, useState, useEffect, useRef, useImperativeHandle, forwardRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { jsx, css } from '@emotion/core';
import { ButtonIcon, InputBase, Label } from "../../";
import { randomString } from "../../../utils";
import { typography } from "../../../styles/typography";
import { boxBorder, borderRadius4px, displayBlock, positionFixed, positionRelative } from "../../../styles/general";
import { useTheme, useColor as colors } from "../../../theme";
const {
  paragraph1
} = typography;
const {
  system: {
    white
  },
  semantic: {
    info
  },
  fill: {
    disabled: fillDisabled,
    'scrollbar-tabbar': scrollbar
  }
} = colors;
const {
  zIndex: zIndexCORE
} = useTheme();
const timing = {};
const DropdownBox = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  className,
  label,
  placeholder,
  startIcon,
  endIcon,
  viewType,
  value,
  openOnClickAt,
  bgColor,
  style,
  inputProps,
  inputStyle,
  labelProps,
  inputRef,
  children,
  zIndex,
  onOpened,
  onClosed,
  onChangeInput,
  delayOnInput
}, reference) => {
  const ref = useRef(null);
  const dropdownBoxRef = useRef(null);
  const [unique] = useState(randomString(6, {
    allowNumber: false,
    allowSymbol: false
  }));
  const [open, setOpen] = useState(false);

  /* Start handler */
  const onTriggerDropdown = e => {
    if (!open) {
      openDropdownBox();
    } else {
      closeDropdownBox(true);
      e.currentTarget.blur();
    }
  };
  const onClickOutsideOfInput = event => {
    if (ref.current && !ref.current.contains(event.target) && dropdownBoxRef.current && !dropdownBoxRef.current.contains(event.target)) {
      closeDropdownBox();
    }
  };
  const pressESCHandler = event => {
    if (event.key === 'Escape') {
      closeDropdownBox();
    }
  };
  const openDropdownBox = () => {
    setOpen(true);
    setTimeout(() => {
      const {
        top,
        left,
        height,
        width
      } = ref.current.getBoundingClientRect();
      let positionTop = top + height + 2;
      dropdownBoxRef.current.style.cssText = `top: ${positionTop}px; left: ${left}px; width: ${width}px;`;
      dropdownBoxRef.current.style.opacity = 1;
      document.addEventListener('mousedown', onClickOutsideOfInput);
      document.addEventListener('keydown', pressESCHandler);
      !!onOpened && onOpened(ref.current);
    }, 10);
  };
  const closeDropdownBox = certain => {
    var _Array$from, _Array$from$classList;
    const portals = document.querySelectorAll('.DGN-UI-Portal');
    if (!certain && portals && portals.length > 1 && Array.from(portals)[portals.length - 1] && !Array.from(portals)[portals.length - 1].contains(ref.current) && !((_Array$from = Array.from(portals)[portals.length - 1]) !== null && _Array$from !== void 0 && (_Array$from$classList = _Array$from.classList) !== null && _Array$from$classList !== void 0 && _Array$from$classList.contains('DGN-UI-Modal'))) {
      return;
    }
    if (dropdownBoxRef.current) {
      dropdownBoxRef.current.style.pointerEvents = `none`;
      dropdownBoxRef.current.style.opacity = 0;
      document.removeEventListener('mousedown', onClickOutsideOfInput);
      timing[unique + 'Box'] = setTimeout(() => {
        clearTimeout(timing[unique + 'Box']);
        timing[unique + 'Box'] = null;
        setOpen(false);
        !!onClosed && onClosed(ref.current);
      }, 300);
    }
  };
  useEffect(() => {
    return () => {
      closeDropdownBox();
    };
  }, []);
  /* End handler */

  useImperativeHandle(reference, () => ({
    open: () => {
      openDropdownBox();
    },
    close: () => {
      closeDropdownBox(true);
    }
  }));
  const renderEndIcon = () => {
    return endIcon && jsx(ButtonIcon, {
      viewType: 'ghost',
      name: endIcon,
      onClick: openOnClickAt === 'icon' ? onTriggerDropdown : null
    });
  };
  return jsx(Fragment, null, jsx("div", {
    ref: ref,
    css: DropdownBoxRootCSS(bgColor),
    className: ['DGN-UI-Dropdown-Box', open && 'dropdown-showing', className].join(' ').trim().replace(/\s+/g, ' '),
    style: style
  }, label ? jsx(Label, {
    ...labelProps
  }, label) : null, jsx(InputBase, {
    ...inputProps,
    className: open ? 'dropdown-focus' : '',
    style: inputStyle,
    viewType: viewType,
    inputRef: inputRef,
    placeholder: placeholder,
    value: value,
    startIcon: startIcon,
    endIcon: renderEndIcon(),
    onChange: onChangeInput,
    delayOnChange: delayOnInput,
    onClick: openOnClickAt === 'full' ? onTriggerDropdown : null
  })), open && /*#__PURE__*/createPortal(jsx("div", {
    ref: dropdownBoxRef,
    className: `DGN-UI-Portal DGN-DropdownBox-${unique}`,
    css: DropdownBoxCSS(zIndex)
  }, children), document.body));
}));

/* Start styled */
const DropdownBoxRootCSS = bgColor => css`
    &.DGN-UI-Dropdown-Box {
        ${displayBlock}
        ${positionRelative}
        min-width: 150px;
        height: max-content;
        .end-icon > .DGN-UI-Icon {
            transition: transform 250ms;
        }
        &.dropdown-showing {
            .end-icon > .DGN-UI-ButtonIcon {
                transform: rotateX(180deg);
            }
        }
        .DGN-UI-InputBase {
            background-color: ${bgColor ? bgColor === true ? fillDisabled : bgColor : 'inherit'};
            &.underlined {
                height: 32px;
            }
            &.dropdown-focus {
                background-color: inherit;
                &::after {
                    border-bottom-color: ${info};
                    transform: scaleX(1);
                }
            }
        }
    }
`;
const DropdownBoxCSS = zIndex => css`
    ${displayBlock};
    ${paragraph1};
    ${positionFixed};
    ${boxBorder};
    ${borderRadius4px};
    background-color: ${white};
    z-index: ${zIndex};
    width: 100%;
    height: auto;
    max-height: 80%;
    overflow: auto;
    padding: 16px;
    opacity: 0;
    transition: opacity 0.3s;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    &::-webkit-scrollbar {
        ${borderRadius4px}
        width: 8px;
        background-color: ${white} !important;
    }
    &::-webkit-scrollbar-thumb {
        ${borderRadius4px}
        mix-blend-mode: normal;
        background-color: ${scrollbar} !important;
        background-clip: content-box;
    }
    .DGN-UI-TreeView {
        padding: 1px;
    }
`;
/* End styled */

DropdownBox.defaultProps = {
  className: '',
  label: '',
  placeholder: '',
  startIcon: 'Search',
  endIcon: 'ArrowDown',
  openOnClickAt: 'icon',
  viewType: 'underlined',
  inputProps: {},
  delayOnInput: 700,
  zIndex: zIndexCORE(1)
};
DropdownBox.propTypes = {
  /** class for dropdown */
  className: PropTypes.string,
  /** label for input */
  label: PropTypes.string,
  /** hints for input */
  placeholder: PropTypes.string,
  /** the content to display in input */
  value: PropTypes.string,
  /** icon at start */
  startIcon: PropTypes.string,
  /** icon at end */
  endIcon: PropTypes.string,
  /** background color of DropdownBox input */
  bgColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** viewType of InputBase */
  viewType: PropTypes.oneOf(['underlined', 'outlined']),
  /** viewType of InputBase */
  openOnClickAt: PropTypes.oneOf(['full', 'icon']),
  /** style inline for dropdown */
  style: PropTypes.object,
  /** props of input in DropdownBox component */
  inputProps: PropTypes.object,
  /** style inline of input in DropdownBox component */
  inputStyle: PropTypes.object,
  /** the function will run when entering input */
  onChangeInput: PropTypes.func,
  /** delay time on input */
  delayOnInput: PropTypes.number,
  /** style inline of label in DropdownBox component */
  labelProps: PropTypes.object,
  /** z-index for DropdownBox component */
  zIndex: PropTypes.number,
  /** the contents in DropdownBox box (Exp: TreeView) */
  children: PropTypes.node.isRequired,
  /** the function will run after open */
  onOpened: PropTypes.func,
  /** the function will run after close */
  onClosed: PropTypes.func
};
export default DropdownBox;